import { useEffect, useMemo, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Button, Surface, Text, useKumoToastManager } from '@cloudflare/kumo'
import type { RoomBookingDto } from '@hub-wifi/shared'
import { ApiError } from '../../api/client'
import { createMyBooking } from '../../api/bookings'
import { listFreeRooms } from '../../api/rooms'
import { describeBookingError } from '../booking-form/utils'
import { useTranslation } from '../../i18n'
import { formatHourMinute, isoAtBuildingTz, wallClockToUtc } from '../../lib/calendar-time'
import { nowStart } from './book-sheet'
import { freeNowOffers, type FreeNowOffer } from './free-now'

/** Ask for the shortest bookable window; each card then sizes its own offer. */
const FETCH_MINUTES = 30

export interface FreeNowStripProps {
  timeZone: string
  now: Date
  /** Bump to re-read the rooms (after a booking made elsewhere on the page). */
  refreshKey?: number
  onBooked: (booking: RoomBookingDto) => void
  /** "More options": opens the Book sheet. */
  onMoreOptions: () => void
}

/**
 * "Free right now": rooms bookable from the next 15-min slot, each with a
 * one-tap Book button (an hour, or less when the room is free for less).
 * Renders nothing when the building is closed, nothing is free, or the
 * lookup fails, so it never pushes the agenda down for no reason.
 */
export function FreeNowStrip({
  timeZone,
  now,
  refreshKey = 0,
  onBooked,
  onMoreOptions,
}: FreeNowStripProps) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth0()
  const toasts = useKumoToastManager()
  const [offers, setOffers] = useState<FreeNowOffer[]>([])
  const [reloadKey, setReloadKey] = useState(0)
  const [bookingRoomId, setBookingRoomId] = useState<string | null>(null)
  // Rooms Exchange declined (409) for a slot: `/rooms/free` only sees hub
  // bookings, so the reload would offer them again. Cleared when the slot moves.
  const [declined, setDeclined] = useState<{ slot: string; roomIds: string[] }>({
    slot: '',
    roomIds: [],
  })

  const start = nowStart(timeZone, now)
  const startMs = start ? wallClockToUtc(start.ymd, start.hm, timeZone).getTime() : null
  const startIso = startMs !== null ? isoAtBuildingTz(startMs, timeZone) : null

  useEffect(() => {
    if (startMs === null || startIso === null) {
      setOffers([])
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const res = await listFreeRooms(startIso, FETCH_MINUTES, token)
        if (cancelled) return
        setOffers(res.buildingOpen ? freeNowOffers(res.rooms, startMs) : [])
      } catch {
        if (!cancelled) setOffers([])
      }
    })()
    return () => {
      cancelled = true
    }
    // startMs follows startIso; refetch when the slot moves or on a reload.
  }, [startIso, refreshKey, reloadKey, getAccessTokenSilently])

  const startHm = start?.hm ?? ''
  const cards = useMemo(() => {
    if (startMs === null) return []
    const declinedIds = declined.slot === startIso ? declined.roomIds : []
    return offers
      .filter((offer) => !declinedIds.includes(offer.room.id))
      .map((offer) => ({
        offer,
        endHm: formatHourMinute(new Date(startMs + offer.minutes * 60_000).toISOString(), timeZone),
        untilHm: offer.room.freeUntil ? formatHourMinute(offer.room.freeUntil, timeZone) : '',
      }))
  }, [offers, startMs, startIso, timeZone, declined])

  if (cards.length === 0) return null

  async function book(offer: FreeNowOffer) {
    // `now` ticks once a minute (less in a background tab): use the real clock,
    // and never book past the room's free run if the slot moved meanwhile.
    const fresh = nowStart(timeZone, new Date())
    if (!fresh || !offer.room.freeUntil) {
      setReloadKey((k) => k + 1)
      return
    }
    const freshMs = wallClockToUtc(fresh.ymd, fresh.hm, timeZone).getTime()
    const endMs = freshMs + offer.minutes * 60_000
    if (endMs > new Date(offer.room.freeUntil).getTime()) {
      toasts.add({ variant: 'error', title: t('dashboard.bookSheet.conflict') })
      setReloadKey((k) => k + 1)
      return
    }
    setBookingRoomId(offer.room.id)
    try {
      const token = await getAccessTokenSilently()
      const dto = await createMyBooking(
        {
          roomId: offer.room.id,
          startAt: isoAtBuildingTz(freshMs, timeZone),
          endAt: isoAtBuildingTz(endMs, timeZone),
        },
        token,
      )
      onBooked(dto)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && startIso) {
        const slot = startIso
        setDeclined((d) => ({
          slot,
          roomIds: d.slot === slot ? [...d.roomIds, offer.room.id] : [offer.room.id],
        }))
      }
      toasts.add({
        variant: 'error',
        title:
          err instanceof ApiError && err.status === 409
            ? t('dashboard.bookSheet.conflict')
            : describeBookingError(err, t),
      })
      // On success the page bumps `refreshKey` instead.
      setReloadKey((k) => k + 1)
    } finally {
      setBookingRoomId(null)
    }
  }

  return (
    <section aria-labelledby="home-free-now" className="flex flex-col gap-3">
      <div className="home-section-head">
        <Text as="h2" variant="heading3" id="home-free-now">
          {t('dashboard.freeNow.title')}
        </Text>
        <Button variant="ghost" size="sm" onClick={onMoreOptions}>
          {t('dashboard.freeNow.moreOptions')}
        </Button>
      </div>
      <ul className="home-free-strip">
        {cards.map(({ offer, endHm, untilHm }) => (
          <Surface as="li" key={offer.room.id} className="home-free-card">
            <div className="home-row__text">
              <span className="home-row__title">{offer.room.name}</span>
              <span className="home-row__meta">
                {t('dashboard.freeNow.meta', { time: untilHm, people: offer.room.capacity })}
              </span>
            </div>
            <Button
              variant="primary"
              loading={bookingRoomId === offer.room.id}
              disabled={bookingRoomId !== null}
              aria-label={t('dashboard.freeNow.bookRoom', {
                room: offer.room.name,
                start: startHm,
                end: endHm,
              })}
              onClick={() => void book(offer)}
            >
              {t('dashboard.freeNow.book', { start: startHm, end: endHm })}
            </Button>
          </Surface>
        ))}
      </ul>
    </section>
  )
}
